"use client"

import { useRef } from "react"

import type { PointerEvent, RefObject } from "react"

const THRESHOLD_PX = 96
const FLICK_PX_MS = 0.5

export function useSwipeDownClose(
  contentRef: RefObject<HTMLElement | null>,
  onClose: () => void
) {
  const session = useRef({
    id: -1,
    startY: 0,
    lastY: 0,
    lastT: 0,
    vel: 0,
    dy: 0
  })

  const onPointerDown = (event: PointerEvent) => {
    if (!window.matchMedia("(max-width: 767px)").matches) return
    if (
      event.target instanceof Element &&
      event.target.closest("a, button, input, textarea, select")
    ) {
      return
    }

    session.current = {
      id: event.pointerId,
      startY: event.clientY,
      lastY: event.clientY,
      lastT: event.timeStamp,
      vel: 0,
      dy: 0
    }
    event.currentTarget.setPointerCapture(event.pointerId)
    contentRef.current?.style.setProperty("transition", "none")
  }

  const onPointerMove = (event: PointerEvent) => {
    const state = session.current
    if (state.id !== event.pointerId) return

    const dt = event.timeStamp - state.lastT
    if (dt > 0) state.vel = (event.clientY - state.lastY) / dt
    state.lastY = event.clientY
    state.lastT = event.timeStamp

    const dy = event.clientY - state.startY
    if (dy <= 0) return
    state.dy = dy
    const el = contentRef.current
    if (el) el.style.transform = `translateY(${dy}px)`
  }

  const onRelease = (event: PointerEvent) => {
    const state = session.current
    if (state.id !== event.pointerId) return
    state.id = -1

    const el = contentRef.current
    if (!el) return
    el.style.removeProperty("transition")

    if (state.dy > THRESHOLD_PX || state.vel > FLICK_PX_MS) {
      el.style.setProperty("--tw-exit-translate-y", `${state.dy}px`)
      onClose()
    } else {
      el.style.transform = ""
    }
    state.dy = 0
    state.vel = 0
  }

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: onRelease,
    onPointerCancel: onRelease
  }
}
